/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { lazy, Suspense, useEffect, useState, type ReactElement } from 'react';
import { useLocation, useRevalidator } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useAuth } from '@/providers/auth';
import type { AppConfig } from '@/types/config';
import { routes } from '@/route-paths';

const LoginModal = lazy(() => import('@/components/login/login-modal'));

/**
 * Prompts a REGISTERED shopper to re-authenticate when the auth middleware has flagged their
 * session with `requiresScopeUpgrade` — i.e. their pre-cutover access token is missing the scope
 * configured in `commerce.api.requiredScope`. A fresh sign-in mints a token carrying the new
 * scope (refresh tokens never pick up newly-granted scopes). Guests are upgraded silently
 * server-side and never reach this component.
 *
 * Checkout-gated: the prompt only opens on the cart and checkout surfaces, where the net-new
 * scope is actually needed. Browsing continues uninterrupted on the old token everywhere else.
 * To make it global instead, drop the `onCheckoutSurface` gate below.
 *
 * Reuses the same `LoginModal` shown for sign-in on the checkout contact step. Lazy-mounted so it
 * adds no bundle/hydration cost until it is actually needed.
 *
 * TBD (messaging): whether to show dedicated copy explaining WHY the shopper is being asked to
 * sign in again (post-cutover security refresh) vs. the default modal title/subtitle. Tracked in
 * docs/migration-tool/app/pages-technical-docs/hybrid-plan-cookie-handling.html §8.
 */
export default function ScopeUpgradeLoginPrompt(): ReactElement | null {
    const auth = useAuth();
    const location = useLocation();
    const config = useConfig<AppConfig>();
    const revalidator = useRevalidator();
    const [dismissed, setDismissed] = useState(false);

    const needsUpgrade = auth?.userType === 'registered' && auth?.requiresScopeUpgrade === true;

    // Re-prompt on each cart/checkout navigation even if previously dismissed, so a shopper who
    // closed it on the cart is asked again when they reach checkout (where the scope is required).
    useEffect(() => {
        setDismissed(false);
    }, [location.pathname]);

    const path = location.pathname;
    const onCheckoutSurface =
        path === routes.cart || path.startsWith(`${routes.cart}/`) || path.startsWith(routes.checkout);

    const open = needsUpgrade && onCheckoutSurface && !dismissed;

    if (!open) return null;

    const isPasswordlessEnabled = Boolean(
        config?.features?.passwordlessLogin?.enabled && config?.commerce?.api?.privateKeyEnabled
    );
    const isSocialLoginEnabled = Boolean(config?.features?.socialLogin?.enabled);
    const otpLength = config?.auth?.otpLength ?? 6;

    return (
        <Suspense fallback={null}>
            <LoginModal
                isOpen={open}
                onOpenChange={(next) => setDismissed(!next)}
                isPasswordlessEnabled={isPasswordlessEnabled}
                isSocialLoginEnabled={isSocialLoginEnabled}
                otpLength={otpLength}
                onSuccess={() => {
                    // Re-run loaders so the middleware re-reads the freshly-minted token; once it
                    // carries the required scope, requiresScopeUpgrade clears and this unmounts.
                    void revalidator.revalidate();
                }}
            />
        </Suspense>
    );
}
